import { CheckCircle2, MessageSquare, MoreVertical, Phone, Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'

interface WhatsAppModalProps {
  isOpen: boolean
  onClose: () => void
  order: any // Usaremos a Order do KanbanCard
  companyName: string
}

export function WhatsAppModal({ isOpen, onClose, order, companyName }: WhatsAppModalProps) {
  const [template, setTemplate] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const loadTemplate = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: dbError } = await supabase
        .from('company_settings')
        .select('whatsapp_template_ready')
        .eq('id', order.company_id)
        .single()

      if (dbError) throw dbError

      const defaultTemplate = `🚗 *${companyName} - Veículo Pronto!*\n\nOlá *[NomeCliente]*!\nSeu veículo *[Veiculo]* (Placa: *[Placa]*) está impecável e pronto para retirada!\n\n📄 *OS:* [OS]\n💰 *Valor Total:* R$ [Total]\n\nAguardamos você em nosso estabelecimento!`

      const rawTemplate = data?.whatsapp_template_ready || defaultTemplate

      // Realiza os replaces
      const finalMsg = rawTemplate
        .replace(/\[NomeCliente\]/g, order.customer_name || 'Cliente')
        .replace(/\[Veiculo\]/g, order.vehicle_model || 'Veículo')
        .replace(/\[Placa\]/g, order.plate || 'SEM PLACA')
        .replace(/\[OS\]/g, String(order.os_number || order.id.substring(0, 6)))
        .replace(/\[Total\]/g, (order.total || 0).toFixed(2).replace('.', ','))
        .replace(/\[NomeEmpresa\]/g, companyName)

      setTemplate(finalMsg)
    } catch (err: any) {
      console.error(err)
      setError('Erro ao carregar o template do WhatsApp.')
    } finally {
      setLoading(false)
    }
  }, [order, companyName])

  useEffect(() => {
    if (isOpen && order) {
      loadTemplate()
    }
  }, [isOpen, order, loadTemplate])

  const formatTextForPreview = (text: string) => {
    // Transforma \n em <br>, e negrito *texto* em <strong>texto</strong> (básico)
    let formatted = text.replace(/\n/g, '<br/>')
    formatted = formatted.replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    return formatted
  }

  const handleSend = async () => {
    setSending(true)
    setError('')
    try {
      // 1. Grava no banco de logs
      const { error: logError } = await supabase.from('whatsapp_logs').insert({
        company_id: order.company_id,
        order_id: order.id,
        sent_to: order.customer_phone || 'N/A',
        trigger_type: 'VEHICLE_READY',
      })

      if (logError) {
        console.error('Erro ao gravar log', logError)
        // Mesmo com erro de log, o fluxo V1 era permissivo, mas na V2 podemos bloquear se necessário.
        // Por enquanto, não vamos bloquear caso apenas o log falhe por falta de setup completo da tabela
      }

      // 2. Abre o WhatsApp Web/App
      const cleanPhone = (order.customer_phone || '').replace(/[^0-9]/g, '')
      const url = `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(template)}`
      window.open(url, '_blank')

      onClose()
    } catch (err: any) {
      setError(err.message || 'Falha ao enviar mensagem.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[450px] p-0 overflow-hidden bg-slate-950 border-slate-800 text-slate-100">
        {/* Header do Modal */}
        <div className="p-4 border-b border-slate-800 bg-slate-900/50 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg text-emerald-400 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Notificar Cliente
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Disparo automatizado de Veículo Pronto</p>
          </div>
        </div>

        {/* Mockup do WhatsApp */}
        <div className="p-6 bg-slate-800 flex justify-center">
          <div className="w-[320px] h-[500px] border-[6px] border-slate-900 rounded-[2.5rem] bg-[#0b141a] shadow-2xl relative overflow-hidden flex flex-col">
            {/* WA Header */}
            <div className="h-[60px] bg-[#202c33] flex items-center px-3 gap-3 shrink-0 z-10 shadow">
              <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center font-bold text-white uppercase text-sm">
                {companyName.substring(0, 2)}
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold text-[15px] leading-tight truncate">
                  {companyName}
                </div>
                <div className="text-[#8696a0] text-[11px] flex items-center gap-1">
                  Conta Comercial Verificada <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                </div>
              </div>
              <Phone className="text-white w-4 h-4 ml-1 opacity-80" />
              <MoreVertical className="text-white w-4 h-4 opacity-80" />
            </div>

            {/* WA Chat Body */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 bg-[#0b141a]/90 custom-scrollbar relative">
              <div className="absolute inset-0 opacity-10 bg-[url('https://i.ibb.co/68v8L5X/wa-bg.png')] bg-cover pointer-events-none z-0"></div>

              <div className="flex justify-center z-10 mb-2">
                <span className="bg-[#182229] text-[#8696a0] text-[11px] uppercase px-3 py-1 rounded-lg shadow-sm">
                  Hoje
                </span>
              </div>

              <div className="flex justify-center z-10 mb-4">
                <span className="bg-[#182229] text-[#ffd279] text-[11px] px-3 py-2 rounded-lg shadow-sm max-w-[90%] text-center leading-tight">
                  <span className="font-bold block mb-0.5">Aviso de Segurança</span>
                  As mensagens para esta empresa são protegidas com criptografia de ponta a ponta.
                </span>
              </div>

              {loading ? (
                <div className="text-center text-emerald-400 text-xs mt-10 z-10 animate-pulse">
                  Carregando template...
                </div>
              ) : (
                <div className="bg-[#005c4b] text-[#e9edef] p-2 px-3 pb-4 rounded-xl rounded-tr-none max-w-[90%] self-end relative z-10 shadow-sm text-[13.5px] leading-relaxed">
                  <div dangerouslySetInnerHTML={{ __html: formatTextForPreview(template) }} />
                  <span className="absolute bottom-1 right-2 text-[#8696a0] text-[10px] font-medium flex items-center gap-1">
                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    <CheckCircle2 className="w-3 h-3 text-[#53bdeb]" />
                  </span>
                </div>
              )}
            </div>

            {/* WA Input Bar */}
            <div className="h-[60px] bg-[#202c33] flex items-center px-3 py-2 gap-2 shrink-0 z-10">
              <div className="flex-1 bg-[#2a3942] h-full rounded-full flex items-center px-4 text-[#8696a0] text-sm font-medium">
                Mensagem
              </div>
              <div className="w-10 h-10 bg-[#00a884] rounded-full flex items-center justify-center shrink-0 shadow-sm">
                <Send className="text-white w-4 h-4 ml-1" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-800 bg-slate-900 flex justify-between items-center gap-4">
          <div className="text-xs text-slate-400">
            {error && <span className="text-red-400">{error}</span>}
            {!order?.customer_phone && (
              <span className="text-amber-400">Aviso: Cliente não possui telefone cadastrado.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={sending}
              className="hover:bg-slate-800 text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || loading || !order?.customer_phone}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Abrindo...' : 'Disparar WhatsApp'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
